<template>
  <div>
    <layout-header></layout-header>
    <layout-sidebar></layout-sidebar>
    <div class="page-wrapper">
      <div class="content container-fluid">
        <div class="page-header">
          <div class="row align-items-center" style="width: -webkit-fill-available;">
            <div class="col-md-8">
              <h3 class="page-title mb-1">Support Abonnements</h3>
              <p class="text-muted mb-3">
                Interfaces Dev pour configurer les plans, options et suivre la facturation des sociétés.
              </p>
              <ul class="nav nav-pills gap-2">
                <li class="nav-item" v-for="tab in tabs" :key="tab.value">
                  <a
                    class="nav-link"
                    :class="{ active: activeTab === tab.value }"
                    href="javascript:void(0);"
                    @click.prevent="setActiveTab(tab.value)"
                  >
                    <i :class="tab.icon" class="me-2"></i>{{ tab.label }}
                    <span v-if="tab.badge && getCollection(tab.value).length" class="badge bg-primary ms-2">
                      {{ getCollection(tab.value).length }}
                    </span>
                  </a>
                </li>
              </ul>
            </div>
            <div class="col-md-4 text-md-end mt-3 mt-md-0 d-flex gap-2 justify-content-md-end">
              <button class="btn btn-outline-secondary" :disabled="isLoading" @click="refreshAll">
                <span v-if="isLoading" class="spinner-border spinner-border-sm me-1"></span>
                <i class="fas fa-sync-alt me-2"></i>Rafraîchir
              </button>
              <button
                v-if="activeTab === 'plans'"
                class="btn btn-primary"
                @click="openPlanModal()"
              >
                <i class="fas fa-plus me-2"></i>Nouveau plan
              </button>
              <button
                v-else-if="activeTab === 'options'"
                class="btn btn-primary"
                @click="openOptionModal()"
              >
                <i class="fas fa-plus me-2"></i>Nouvelle option
              </button>
              <button
                v-else-if="activeTab === 'invoices'"
                class="btn btn-primary"
                @click="openInvoiceModal()"
              >
                <i class="fas fa-plus me-2"></i>Nouvelle facture
              </button>
              <button
                v-else-if="activeTab === 'payments'"
                class="btn btn-primary"
                @click="openPaymentModal()"
              >
                <i class="fas fa-plus me-2"></i>Nouveau paiement
              </button>
              <button
                v-else-if="activeTab === 'reminders'"
                class="btn btn-primary"
                @click="openReminderModal()"
              >
                <i class="fas fa-plus me-2"></i>Nouveau rappel
              </button>
              <button
                v-else-if="activeTab === 'usage'"
                class="btn btn-primary"
                @click="openUsageModal()"
              >
                <i class="fas fa-plus me-2"></i>Nouvelle entrée usage
              </button>
            </div>
          </div>
        </div>

        <div class="card">
          <div class="card-body">
            <div v-if="isLoading" class="text-center py-5">
              <span class="spinner-border text-primary" role="status" aria-hidden="true"></span>
            </div>
            <template v-else>
              <div v-if="activeTab === 'plans'">
                <h5 class="mb-3">Plans d'abonnement</h5>
                <div class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Nom</th>
                        <th>Mensuel</th>
                        <th>Annuel</th>
                        <th>Utilisateurs inclus</th>
                        <th>Admins inclus</th>
                        <th>Statut</th>
                        <th class="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="plan in pagedCollection" :key="plan.id">
                        <td class="fw-semibold">{{ plan.nom }}</td>
                        <td>{{ formatCurrency(plan.prix_mensuel) }}</td>
                        <td>{{ formatCurrency(plan.prix_annuel) }}</td>
                        <td>{{ plan.utilisateurs_inclus ?? "—" }}</td>
                        <td>{{ plan.administrateurs_inclus ?? "—" }}</td>
                        <td>
                          <span
                            class="badge"
                            :class="plan.actif ? 'bg-success' : 'bg-secondary'"
                          >{{ plan.actif ? "Actif" : "Inactif" }}</span>
                        </td>
                        <td class="text-center">
                          <button class="btn btn-sm btn-outline-primary me-2" @click="openPlanModal(plan)">
                            <i class="fas fa-edit"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-danger" @click="confirmDeletePlan(plan)">
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                      <tr v-if="!plans.length">
                        <td colspan="7" class="text-center text-muted py-4">Aucun plan enregistré.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-else-if="activeTab === 'options'">
                <h5 class="mb-3">Options supplémentaires</h5>
                <div class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Nom</th>
                        <th>Description</th>
                        <th>Mensuel</th>
                        <th>Prix unique</th>
                        <th>Abonnements liés</th>
                        <th class="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="option in pagedCollection" :key="option.id">
                        <td class="fw-semibold">{{ option.nom }}</td>
                        <td>{{ option.description || "—" }}</td>
                        <td>{{ formatCurrency(option.prix_mensuel) }}</td>
                        <td>{{ formatCurrency(option.prix_unique) }}</td>
                        <td>
                          <span class="badge bg-info">{{ option.abonnements_count || 0 }}</span>
                        </td>
                        <td class="text-center">
                          <button class="btn btn-sm btn-outline-primary me-2" @click="openOptionModal(option)">
                            <i class="fas fa-edit"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteOption(option)">
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                      <tr v-if="!options.length">
                        <td colspan="6" class="text-center text-muted py-4">Aucune option enregistrée.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-else-if="activeTab === 'invoices'">
                <h5 class="mb-3">Factures d'abonnement</h5>
                <div class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Numéro</th>
                        <th>Société</th>
                        <th>Formule</th>
                        <th>Montant TTC</th>
                        <th>Statut</th>
                        <th>Emission</th>
                        <th>Échéance</th>
                        <th class="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="invoice in pagedCollection" :key="invoice.id">
                        <td class="fw-semibold">{{ invoice.numero }}</td>
                        <td>{{ invoice.societe?.societe_nom || "—" }}</td>
                        <td>{{ invoice.abonnement?.nom || "—" }}</td>
                        <td>{{ formatCurrency(invoice.montant_ttc) }}</td>
                        <td><span class="badge bg-secondary">{{ invoice.statut || "N/A" }}</span></td>
                        <td>{{ formatDate(invoice.date_emission) }}</td>
                        <td>{{ formatDate(invoice.date_echeance) }}</td>
                        <td class="text-center">
                          <button class="btn btn-sm btn-outline-primary me-2" @click="openInvoiceModal(invoice)">
                            <i class="fas fa-edit"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteInvoice(invoice)">
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                      <tr v-if="!invoices.length">
                        <td colspan="8" class="text-center text-muted py-4">Aucune facture enregistrée.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-else-if="activeTab === 'payments'">
                <h5 class="mb-3">Paiements</h5>
                <div class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Référence</th>
                        <th>Société</th>
                        <th>Formule</th>
                        <th>Montant</th>
                        <th>Mode</th>
                        <th>Statut</th>
                        <th>Date</th>
                        <th class="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="payment in pagedCollection" :key="payment.id">
                        <td>{{ payment.reference || "—" }}</td>
                        <td>{{ payment.societe?.societe_nom || "—" }}</td>
                        <td>{{ payment.abonnement?.nom || "—" }}</td>
                        <td>{{ formatCurrency(payment.montant) }}</td>
                        <td>{{ payment.mode_paiement || "—" }}</td>
                        <td><span class="badge bg-secondary">{{ payment.statut || "N/A" }}</span></td>
                        <td>{{ formatDate(payment.date_paiement) }}</td>
                        <td class="text-center">
                          <button class="btn btn-sm btn-outline-primary me-2" @click="openPaymentModal(payment)">
                            <i class="fas fa-edit"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-danger" @click="confirmDeletePayment(payment)">
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                      <tr v-if="!payments.length">
                        <td colspan="8" class="text-center text-muted py-4">Aucun paiement enregistré.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-else-if="activeTab === 'reminders'">
                <h5 class="mb-3">Rappels envoyés</h5>
                <div class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Type</th>
                        <th>Canal</th>
                        <th>Société</th>
                        <th>Formule</th>
                        <th>Date d'envoi</th>
                        <th>Statut</th>
                        <th class="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="reminder in pagedCollection" :key="reminder.id">
                        <td class="fw-semibold">{{ reminder.type }}</td>
                        <td>{{ reminder.canal }}</td>
                        <td>{{ reminder.societe?.societe_nom || "—" }}</td>
                        <td>{{ reminder.abonnement?.nom || "—" }}</td>
                        <td>{{ formatDate(reminder.date_envoi) }}</td>
                        <td><span class="badge bg-secondary">{{ reminder.statut || "N/A" }}</span></td>
                        <td class="text-center">
                          <button class="btn btn-sm btn-outline-primary me-2" @click="openReminderModal(reminder)">
                            <i class="fas fa-edit"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteReminder(reminder)">
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                      <tr v-if="!reminders.length">
                        <td colspan="7" class="text-center text-muted py-4">Aucun rappel enregistré.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <div v-else-if="activeTab === 'usage'">
                <h5 class="mb-3">Journaux d'utilisation</h5>
                <div class="table-responsive">
                  <table class="table table-hover align-middle">
                    <thead class="table-light">
                      <tr>
                        <th>Metric</th>
                        <th>Valeur</th>
                        <th>Société</th>
                        <th>Formule</th>
                        <th>Date</th>
                        <th class="text-center">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr v-for="log in pagedCollection" :key="log.id">
                        <td class="fw-semibold">{{ log.metric }}</td>
                        <td>{{ log.valeur ?? "—" }}</td>
                        <td>{{ log.societe?.societe_nom || "—" }}</td>
                        <td>{{ log.abonnement?.nom || "—" }}</td>
                        <td>{{ formatDate(log.date_snapshot) }}</td>
                        <td class="text-center">
                          <button class="btn btn-sm btn-outline-primary me-2" @click="openUsageModal(log)">
                            <i class="fas fa-edit"></i>
                          </button>
                          <button class="btn btn-sm btn-outline-danger" @click="confirmDeleteUsage(log)">
                            <i class="fas fa-trash-alt"></i>
                          </button>
                        </td>
                      </tr>
                      <tr v-if="!usageLogs.length">
                        <td colspan="6" class="text-center text-muted py-4">Aucun journal enregistré.</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              <!-- pagination controls (shared for active tab) -->
              <nav v-if="totalItems > 0" class="mt-3">
                <div class="d-flex align-items-center justify-content-between">
                  <div class="d-flex align-items-center gap-2">
                    <label class="small text-muted mb-0">Afficher</label>
                    <select class="form-select form-select-sm" v-model.number="pageSize" @change="changePageSize" style="width: auto;">
                      <option :value="10">10</option>
                      <option :value="25">25</option>
                      <option :value="50">50</option>
                    </select>
                    <span class="small text-muted"> / {{ totalItems }} éléments</span>
                  </div>

                  <ul class="pagination mb-0">
                    <li class="page-item" :class="{ disabled: page <= 1 }">
                      <button class="page-link" @click="goToPage(page - 1)" :disabled="page <= 1">Précédent</button>
                    </li>
                    <li v-for="p in visiblePages" :key="p" class="page-item" :class="{ active: p === page }">
                      <button class="page-link" @click="goToPage(p)">{{ p }}</button>
                    </li>
                    <li class="page-item" :class="{ disabled: page >= totalPages }">
                      <button class="page-link" @click="goToPage(page + 1)" :disabled="page >= totalPages">Suivant</button>
                    </li>
                  </ul>
                </div>
              </nav>
            </template>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Plan modal -->
  <div class="modal fade" ref="planModalRef" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <form @submit.prevent="submitPlanForm">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingPlan ? "Modifier le plan" : "Nouveau plan" }}
            </h5>
            <button type="button" class="btn-close" @click="hidePlanModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Nom</label>
              <input v-model="planForm.nom" type="text" class="form-control" required maxlength="100" />
            </div>
            <div class="mb-3">
              <label class="form-label">Description</label>
              <textarea v-model="planForm.description" class="form-control" rows="2" maxlength="500"></textarea>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Prix mensuel</label>
                  <input v-model.number="planForm.prix_mensuel" type="number" min="0" step="0.01" class="form-control" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Prix annuel</label>
                  <input v-model.number="planForm.prix_annuel" type="number" min="0" step="0.01" class="form-control" />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Utilisateurs inclus</label>
                  <input v-model.number="planForm.utilisateurs_inclus" type="number" min="0" class="form-control" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Admins inclus</label>
                  <input v-model.number="planForm.administrateurs_inclus" type="number" min="0" class="form-control" />
                </div>
              </div>
            </div>
            <div class="form-check form-switch">
              <input
                class="form-check-input"
                type="checkbox"
                role="switch"
                id="plan-actif"
                v-model="planForm.actif"
              />
              <label class="form-check-label" for="plan-actif">Plan actif</label>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="hidePlanModal" :disabled="planSubmitting">
              Annuler
            </button>
            <button type="submit" class="btn btn-primary" :disabled="planSubmitting">
              <span v-if="planSubmitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingPlan ? "Mettre à jour" : "Créer" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Option modal -->
  <div class="modal fade" ref="optionModalRef" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <form @submit.prevent="submitOptionForm">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingOption ? "Modifier l'option" : "Nouvelle option" }}
            </h5>
            <button type="button" class="btn-close" @click="hideOptionModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Nom</label>
              <input v-model="optionForm.nom" type="text" class="form-control" required maxlength="100" />
            </div>
            <div class="mb-3">
              <label class="form-label">Description</label>
              <textarea v-model="optionForm.description" class="form-control" rows="2" maxlength="500"></textarea>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Prix mensuel</label>
                  <input v-model.number="optionForm.prix_mensuel" type="number" min="0" step="0.01" class="form-control" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Prix unique</label>
                  <input v-model.number="optionForm.prix_unique" type="number" min="0" step="0.01" class="form-control" />
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="hideOptionModal" :disabled="optionSubmitting">
              Annuler
            </button>
            <button type="submit" class="btn btn-primary" :disabled="optionSubmitting">
              <span v-if="optionSubmitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingOption ? "Mettre à jour" : "Créer" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Invoice modal -->
  <div class="modal fade" ref="invoiceModalRef" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <form @submit.prevent="submitInvoiceForm">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingInvoice ? "Modifier la facture" : "Nouvelle facture" }}
            </h5>
            <button type="button" class="btn-close" @click="hideInvoiceModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Société</label>
              <select v-model.number="invoiceForm.societe_id" class="form-select" required>
                <option value="" disabled>Sélectionnez une société</option>
                <option v-for="soc in societes" :key="soc.id" :value="soc.id">
                  {{ formatSocieteLabel(soc) }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Abonnement</label>
              <select v-model.number="invoiceForm.abonnement_id" class="form-select" required>
                <option value="" disabled>Sélectionnez un abonnement</option>
                <option v-for="ab in abonnementOptions" :key="ab.id" :value="ab.id">
                  {{ ab.nom }} (ID: {{ ab.id }})
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Numéro</label>
              <input v-model="invoiceForm.numero" type="text" class="form-control" required maxlength="100" />
            </div>
            <div class="row">
              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label">Montant HT</label>
                  <input v-model.number="invoiceForm.montant_ht" type="number" step="0.01" min="0" class="form-control" required />
                </div>
              </div>
              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label">Montant TVA</label>
                  <input v-model.number="invoiceForm.montant_tva" type="number" step="0.01" min="0" class="form-control" required />
                </div>
              </div>
              <div class="col-md-4">
                <div class="mb-3">
                  <label class="form-label">Montant TTC</label>
                  <input v-model.number="invoiceForm.montant_ttc" type="number" step="0.01" min="0" class="form-control" required />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Statut</label>
                  <select v-model="invoiceForm.statut" class="form-select">
                    <option value="">Non défini</option>
                    <option
                      v-for="status in invoiceStatusOptions"
                      :key="status.value"
                      :value="status.value"
                    >
                      {{ status.label }}
                    </option>
                  </select>
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Date d'émission</label>
                  <input v-model="invoiceForm.date_emission" type="date" class="form-control" />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Date d'échéance</label>
                  <input v-model="invoiceForm.date_echeance" type="date" class="form-control" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Notes</label>
                  <textarea v-model="invoiceForm.notes" class="form-control" rows="2" maxlength="500"></textarea>
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="hideInvoiceModal" :disabled="invoiceSubmitting">
              Annuler
            </button>
            <button type="submit" class="btn btn-primary" :disabled="invoiceSubmitting">
              <span v-if="invoiceSubmitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingInvoice ? "Mettre à jour" : "Créer" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Payment modal -->
  <div class="modal fade" ref="paymentModalRef" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <form @submit.prevent="submitPaymentForm">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingPayment ? "Modifier le paiement" : "Nouveau paiement" }}
            </h5>
            <button type="button" class="btn-close" @click="hidePaymentModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Société</label>
              <select v-model.number="paymentForm.societe_id" class="form-select" required>
                <option value="" disabled>Sélectionnez une société</option>
                <option v-for="soc in societes" :key="soc.id" :value="soc.id">
                  {{ formatSocieteLabel(soc) }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Abonnement</label>
              <select v-model.number="paymentForm.abonnement_id" class="form-select" required>
                <option value="" disabled>Sélectionnez un abonnement</option>
                <option v-for="ab in abonnementOptions" :key="ab.id" :value="ab.id">
                  {{ ab.nom }} (ID: {{ ab.id }})
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Facture (optionnel)</label>
              <select v-model="paymentForm.abonnement_invoice_id" class="form-select">
                <option value="">Aucune</option>
                <option v-for="invoice in invoices" :key="invoice.id" :value="invoice.id">
                  {{ invoice.numero }} (ID: {{ invoice.id }})
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Montant</label>
              <input v-model.number="paymentForm.montant" type="number" step="0.01" min="0" class="form-control" required />
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Mode de paiement</label>
                  <input v-model="paymentForm.mode_paiement" type="text" class="form-control" maxlength="100" required />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Référence</label>
                  <input v-model="paymentForm.reference" type="text" class="form-control" maxlength="150" />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Statut</label>
                  <input v-model="paymentForm.statut" type="text" class="form-control" maxlength="50" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Date de paiement</label>
                  <input v-model="paymentForm.date_paiement" type="date" class="form-control" />
                </div>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label">Notes</label>
              <textarea v-model="paymentForm.notes" class="form-control" rows="2" maxlength="500"></textarea>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="hidePaymentModal" :disabled="paymentSubmitting">
              Annuler
            </button>
            <button type="submit" class="btn btn-primary" :disabled="paymentSubmitting">
              <span v-if="paymentSubmitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingPayment ? "Mettre à jour" : "Créer" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Reminder modal -->
  <div class="modal fade" ref="reminderModalRef" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <form @submit.prevent="submitReminderForm">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingReminder ? "Modifier le rappel" : "Nouveau rappel" }}
            </h5>
            <button type="button" class="btn-close" @click="hideReminderModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Société</label>
              <select v-model.number="reminderForm.societe_id" class="form-select" required>
                <option value="" disabled>Sélectionnez une société</option>
                <option v-for="soc in societes" :key="soc.id" :value="soc.id">
                  {{ formatSocieteLabel(soc) }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Abonnement</label>
              <select v-model.number="reminderForm.abonnement_id" class="form-select" required>
                <option value="" disabled>Sélectionnez un abonnement</option>
                <option v-for="ab in abonnementOptions" :key="ab.id" :value="ab.id">
                  {{ ab.nom }} (ID: {{ ab.id }})
                </option>
              </select>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Type</label>
                  <input v-model="reminderForm.type" type="text" class="form-control" maxlength="100" required />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Canal</label>
                  <input v-model="reminderForm.canal" type="text" class="form-control" maxlength="50" required />
                </div>
              </div>
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Date d'envoi</label>
                  <input v-model="reminderForm.date_envoi" type="datetime-local" class="form-control" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Statut</label>
                  <input v-model="reminderForm.statut" type="text" class="form-control" maxlength="50" />
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="hideReminderModal" :disabled="reminderSubmitting">
              Annuler
            </button>
            <button type="submit" class="btn btn-primary" :disabled="reminderSubmitting">
              <span v-if="reminderSubmitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingReminder ? "Mettre à jour" : "Créer" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>

  <!-- Usage log modal -->
  <div class="modal fade" ref="usageModalRef" tabindex="-1" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <form @submit.prevent="submitUsageForm">
          <div class="modal-header">
            <h5 class="modal-title">
              {{ editingUsage ? "Modifier l'entrée" : "Nouvelle entrée usage" }}
            </h5>
            <button type="button" class="btn-close" @click="hideUsageModal"></button>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label">Société</label>
              <select v-model.number="usageForm.societe_id" class="form-select" required>
                <option value="" disabled>Sélectionnez une société</option>
                <option v-for="soc in societes" :key="soc.id" :value="soc.id">
                  {{ formatSocieteLabel(soc) }}
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Abonnement</label>
              <select v-model.number="usageForm.abonnement_id" class="form-select" required>
                <option value="" disabled>Sélectionnez un abonnement</option>
                <option v-for="ab in abonnementOptions" :key="ab.id" :value="ab.id">
                  {{ ab.nom }} (ID: {{ ab.id }})
                </option>
              </select>
            </div>
            <div class="mb-3">
              <label class="form-label">Métrique</label>
              <input v-model="usageForm.metric" type="text" class="form-control" maxlength="100" required />
            </div>
            <div class="row">
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Valeur</label>
                  <input v-model.number="usageForm.valeur" type="number" min="0" class="form-control" />
                </div>
              </div>
              <div class="col-md-6">
                <div class="mb-3">
                  <label class="form-label">Date</label>
                  <input v-model="usageForm.date_snapshot" type="date" class="form-control" />
                </div>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-outline-secondary" @click="hideUsageModal" :disabled="usageSubmitting">
              Annuler
            </button>
            <button type="submit" class="btn btn-primary" :disabled="usageSubmitting">
              <span v-if="usageSubmitting" class="spinner-border spinner-border-sm me-2"></span>
              {{ editingUsage ? "Mettre à jour" : "Créer" }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, onMounted, onBeforeUnmount, reactive, ref, watch } from "vue";
import { Modal } from "bootstrap";
import { toast } from "vue3-toastify";
import {
  fetchPlans,
  createPlan,
  updatePlan,
  deletePlan,
  fetchOptions,
  createOption,
  updateOption,
  deleteOption,
  fetchInvoices,
  createInvoice,
  updateInvoice,
  deleteInvoice,
  fetchPayments,
  createPayment,
  updatePayment,
  deletePayment,
  fetchReminders,
  createReminder,
  updateReminder,
  deleteReminder,
  fetchUsageLogs,
  createUsageLog,
  updateUsageLog,
  deleteUsageLog,
} from "@/services/devAbonnementService";
import { fetchAllSocietes } from "@/services/devSupportService";

const tabs = [
  { value: "plans", label: "Plans", icon: "fas fa-layer-group", badge: true },
  { value: "options", label: "Options", icon: "fas fa-puzzle-piece", badge: true },
  { value: "invoices", label: "Factures", icon: "fas fa-file-invoice" },
  { value: "payments", label: "Paiements", icon: "fas fa-credit-card" },
  { value: "reminders", label: "Rappels", icon: "fas fa-bell" },
  { value: "usage", label: "Utilisation", icon: "fas fa-chart-bar" },
];

const invoiceStatusOptions = [
  { value: "draft", label: "Brouillon" },
  { value: "pending", label: "En attente" },
  { value: "paid", label: "Payée" },
  { value: "overdue", label: "En retard" },
  { value: "cancelled", label: "Annulée" },
];

const activeTab = ref("plans");
const isLoading = ref(false);

const plans = ref([]);
const options = ref([]);
const invoices = ref([]);
const payments = ref([]);
const reminders = ref([]);
const usageLogs = ref([]);
const societes = ref([]);
const abonnements = ref([]);

const abonnementOptions = computed(() =>
  abonnements.value.map((item) => ({
    id: item.id,
    nom: `${item.nom || "Abonnement"} · Société ${item.societe_id}`,
  }))
);

const planModalRef = ref(null);
const optionModalRef = ref(null);
const invoiceModalRef = ref(null);
const paymentModalRef = ref(null);
const reminderModalRef = ref(null);
const usageModalRef = ref(null);
let planModalInstance = null;
let optionModalInstance = null;
let invoiceModalInstance = null;
let paymentModalInstance = null;
let reminderModalInstance = null;
let usageModalInstance = null;
const modalOptions = { backdrop: "static" };

const editingPlan = ref(null);
const editingOption = ref(null);
const editingInvoice = ref(null);
const editingPayment = ref(null);
const editingReminder = ref(null);
const editingUsage = ref(null);
const planSubmitting = ref(false);
const optionSubmitting = ref(false);
const invoiceSubmitting = ref(false);
const paymentSubmitting = ref(false);
const reminderSubmitting = ref(false);
const usageSubmitting = ref(false);

const planForm = reactive({
  nom: "",
  description: "",
  prix_mensuel: null,
  prix_annuel: null,
  utilisateurs_inclus: null,
  administrateurs_inclus: null,
  actif: true,
});

const optionForm = reactive({
  nom: "",
  description: "",
  prix_mensuel: null,
  prix_unique: null,
});

const invoiceForm = reactive({
  abonnement_id: "",
  societe_id: "",
  numero: "",
  montant_ht: null,
  montant_tva: null,
  montant_ttc: null,
  statut: "",
  date_emission: "",
  date_echeance: "",
  notes: "",
});

const paymentForm = reactive({
  abonnement_id: "",
  societe_id: "",
  abonnement_invoice_id: "",
  montant: null,
  mode_paiement: "",
  reference: "",
  statut: "",
  date_paiement: "",
  notes: "",
});

const reminderForm = reactive({
  abonnement_id: "",
  societe_id: "",
  type: "",
  canal: "",
  date_envoi: "",
  statut: "",
});

const usageForm = reactive({
  abonnement_id: "",
  societe_id: "",
  metric: "",
  valeur: null,
  date_snapshot: "",
});

const resetPlanForm = () => {
  planForm.nom = "";
  planForm.description = "";
  planForm.prix_mensuel = null;
  planForm.prix_annuel = null;
  planForm.utilisateurs_inclus = null;
  planForm.administrateurs_inclus = null;
  planForm.actif = true;
};

const resetOptionForm = () => {
  optionForm.nom = "";
  optionForm.description = "";
  optionForm.prix_mensuel = null;
  optionForm.prix_unique = null;
};

const resetInvoiceForm = () => {
  invoiceForm.abonnement_id = "";
  invoiceForm.societe_id = "";
  invoiceForm.numero = "";
  invoiceForm.montant_ht = null;
  invoiceForm.montant_tva = null;
  invoiceForm.montant_ttc = null;
  invoiceForm.statut = "";
  invoiceForm.date_emission = "";
  invoiceForm.date_echeance = "";
  invoiceForm.notes = "";
};

const resetPaymentForm = () => {
  paymentForm.abonnement_id = "";
  paymentForm.societe_id = "";
  paymentForm.abonnement_invoice_id = "";
  paymentForm.montant = null;
  paymentForm.mode_paiement = "";
  paymentForm.reference = "";
  paymentForm.statut = "";
  paymentForm.date_paiement = "";
  paymentForm.notes = "";
};

const resetReminderForm = () => {
  reminderForm.abonnement_id = "";
  reminderForm.societe_id = "";
  reminderForm.type = "";
  reminderForm.canal = "";
  reminderForm.date_envoi = "";
  reminderForm.statut = "";
};

const resetUsageForm = () => {
  usageForm.abonnement_id = "";
  usageForm.societe_id = "";
  usageForm.metric = "";
  usageForm.valeur = null;
  usageForm.date_snapshot = "";
};

const loadPlans = async () => {
  try {
    const response = await fetchPlans();
    plans.value = Array.isArray(response?.data) ? response.data : [];
  } catch (error) {
    handleError(error, "Impossible de charger les plans.");
  }
};

const loadOptions = async () => {
  try {
    const response = await fetchOptions();
    options.value = Array.isArray(response?.data) ? response.data : [];
  } catch (error) {
    handleError(error, "Impossible de charger les options.");
  }
};

const loadInvoices = async () => {
  try {
    const response = await fetchInvoices();
    invoices.value = Array.isArray(response?.data) ? response.data : [];
  } catch (error) {
    handleError(error, "Impossible de charger les factures.");
  }
};

const loadPayments = async () => {
  try {
    const response = await fetchPayments();
    payments.value = Array.isArray(response?.data) ? response.data : [];
  } catch (error) {
    handleError(error, "Impossible de charger les paiements.");
  }
};

const loadReminders = async () => {
  try {
    const response = await fetchReminders();
    reminders.value = Array.isArray(response?.data) ? response.data : [];
  } catch (error) {
    handleError(error, "Impossible de charger les rappels.");
  }
};

const loadUsageLogs = async () => {
  try {
    const response = await fetchUsageLogs();
    usageLogs.value = Array.isArray(response?.data) ? response.data : [];
  } catch (error) {
    handleError(error, "Impossible de charger les journaux.");
  }
};

const loadReferenceData = async () => {
  try {
    const response = await fetchAllSocietes();
    const data = Array.isArray(response?.data) ? response.data : [];
    societes.value = data;
    abonnements.value = data.flatMap((soc) =>
      Array.isArray(soc?.abonnements) ? soc.abonnements : []
    );
  } catch (error) {
    handleError(error, "Impossible de charger les sociétés.");
  }
};

const refreshAll = async () => {
  isLoading.value = true;
  try {
    await Promise.all([
      loadPlans(),
      loadOptions(),
      loadReferenceData(),
      loadInvoices(),
      loadPayments(),
      loadReminders(),
      loadUsageLogs(),
    ]);
  } finally {
    isLoading.value = false;
  }
};

const openPlanModal = (plan = null) => {
  if (plan) {
    editingPlan.value = plan;
    planForm.nom = plan.nom;
    planForm.description = plan.description;
    planForm.prix_mensuel = plan.prix_mensuel;
    planForm.prix_annuel = plan.prix_annuel;
    planForm.utilisateurs_inclus = plan.utilisateurs_inclus;
    planForm.administrateurs_inclus = plan.administrateurs_inclus;
    planForm.actif = Boolean(plan.actif);
  } else {
    editingPlan.value = null;
    resetPlanForm();
  }

  planModalInstance?.show();
};

const hidePlanModal = () => {
  planModalInstance?.hide();
};

const submitPlanForm = async () => {
  planSubmitting.value = true;
  const payload = {
    nom: planForm.nom,
    description: planForm.description,
    prix_mensuel: planForm.prix_mensuel,
    prix_annuel: planForm.prix_annuel,
    utilisateurs_inclus: planForm.utilisateurs_inclus,
    administrateurs_inclus: planForm.administrateurs_inclus,
    actif: planForm.actif,
  };

  try {
    if (editingPlan.value) {
      await updatePlan(editingPlan.value.id, payload);
      toast.success("Plan mis à jour avec succès.");
    } else {
      await createPlan(payload);
      toast.success("Plan créé avec succès.");
    }
    hidePlanModal();
    await loadPlans();
  } catch (error) {
    handleError(error, "Une erreur est survenue lors de l'enregistrement du plan.");
  } finally {
    planSubmitting.value = false;
  }
};

const confirmDeletePlan = async (plan) => {
  if (!window.confirm(`Supprimer le plan "${plan.nom}" ?`)) return;
  try {
    await deletePlan(plan.id);
    toast.success("Plan supprimé avec succès.");
    await loadPlans();
  } catch (error) {
    handleError(error, "Impossible de supprimer le plan.");
  }
};

const openOptionModal = (option = null) => {
  if (option) {
    editingOption.value = option;
    optionForm.nom = option.nom;
    optionForm.description = option.description;
    optionForm.prix_mensuel = option.prix_mensuel;
    optionForm.prix_unique = option.prix_unique;
  } else {
    editingOption.value = null;
    resetOptionForm();
  }

  optionModalInstance?.show();
};

const hideOptionModal = () => {
  optionModalInstance?.hide();
};

const submitOptionForm = async () => {
  optionSubmitting.value = true;
  const payload = {
    nom: optionForm.nom,
    description: optionForm.description,
    prix_mensuel: optionForm.prix_mensuel,
    prix_unique: optionForm.prix_unique,
  };

  try {
    if (editingOption.value) {
      await updateOption(editingOption.value.id, payload);
      toast.success("Option mise à jour avec succès.");
    } else {
      await createOption(payload);
      toast.success("Option créée avec succès.");
    }
    hideOptionModal();
    await loadOptions();
  } catch (error) {
    handleError(error, "Une erreur est survenue lors de l'enregistrement de l'option.");
  } finally {
    optionSubmitting.value = false;
  }
};

const confirmDeleteOption = async (option) => {
  if (!window.confirm(`Supprimer l'option "${option.nom}" ?`)) return;
  try {
    await deleteOption(option.id);
    toast.success("Option supprimée avec succès.");
    await loadOptions();
  } catch (error) {
    handleError(error, "Impossible de supprimer l'option.");
  }
};

const openInvoiceModal = (invoice = null) => {
  if (invoice) {
    editingInvoice.value = invoice;
    invoiceForm.abonnement_id = invoice.abonnement_id ?? invoice.abonnement?.id ?? "";
    invoiceForm.societe_id = invoice.societe_id ?? invoice.societe?.id ?? "";
    invoiceForm.numero = invoice.numero ?? "";
    invoiceForm.montant_ht = invoice.montant_ht;
    invoiceForm.montant_tva = invoice.montant_tva;
    invoiceForm.montant_ttc = invoice.montant_ttc;
    invoiceForm.statut = invoice.statut ?? "";
    invoiceForm.date_emission = toDateInput(invoice.date_emission);
    invoiceForm.date_echeance = toDateInput(invoice.date_echeance);
    invoiceForm.notes = invoice.notes ?? "";
  } else {
    editingInvoice.value = null;
    resetInvoiceForm();
  }

  invoiceModalInstance?.show();
};

const hideInvoiceModal = () => {
  invoiceModalInstance?.hide();
};

const submitInvoiceForm = async () => {
  invoiceSubmitting.value = true;
  const payload = {
    abonnement_id: Number(invoiceForm.abonnement_id),
    societe_id: Number(invoiceForm.societe_id),
    numero: invoiceForm.numero,
    montant_ht: toNumberOrZero(invoiceForm.montant_ht),
    montant_tva: toNumberOrZero(invoiceForm.montant_tva),
    montant_ttc: toNumberOrZero(invoiceForm.montant_ttc),
    statut: emptyToNull(invoiceForm.statut),
    date_emission: emptyToNull(invoiceForm.date_emission),
    date_echeance: emptyToNull(invoiceForm.date_echeance),
    notes: emptyToNull(invoiceForm.notes),
  };

  try {
    if (editingInvoice.value) {
      await updateInvoice(editingInvoice.value.id, payload);
      toast.success("Facture mise à jour avec succès.");
    } else {
      await createInvoice(payload);
      toast.success("Facture créée avec succès.");
    }
    hideInvoiceModal();
    await loadInvoices();
  } catch (error) {
    handleError(error, "Une erreur est survenue lors de l'enregistrement de la facture.");
  } finally {
    invoiceSubmitting.value = false;
  }
};

const confirmDeleteInvoice = async (invoice) => {
  if (!window.confirm(`Supprimer la facture "${invoice.numero}" ?`)) return;
  try {
    await deleteInvoice(invoice.id);
    toast.success("Facture supprimée avec succès.");
    await loadInvoices();
  } catch (error) {
    handleError(error, "Impossible de supprimer la facture.");
  }
};

const openPaymentModal = (payment = null) => {
  if (payment) {
    editingPayment.value = payment;
    paymentForm.abonnement_id = payment.abonnement_id ?? payment.abonnement?.id ?? "";
    paymentForm.societe_id = payment.societe_id ?? payment.societe?.id ?? "";
    const invoiceId = payment.abonnement_invoice_id ?? payment.invoice?.id ?? "";
    paymentForm.abonnement_invoice_id =
      invoiceId === "" || invoiceId === null || invoiceId === undefined ? "" : String(invoiceId);
    paymentForm.montant = payment.montant;
    paymentForm.mode_paiement = payment.mode_paiement ?? "";
    paymentForm.reference = payment.reference ?? "";
    paymentForm.statut = payment.statut ?? "";
    paymentForm.date_paiement = toDateInput(payment.date_paiement);
    paymentForm.notes = payment.notes ?? "";
  } else {
    editingPayment.value = null;
    resetPaymentForm();
  }

  paymentModalInstance?.show();
};

const hidePaymentModal = () => {
  paymentModalInstance?.hide();
};

const submitPaymentForm = async () => {
  paymentSubmitting.value = true;
  const payload = {
    abonnement_id: Number(paymentForm.abonnement_id),
    societe_id: Number(paymentForm.societe_id),
    abonnement_invoice_id: toNumberOrNull(paymentForm.abonnement_invoice_id),
    montant: toNumberOrZero(paymentForm.montant),
    mode_paiement: paymentForm.mode_paiement,
    reference: emptyToNull(paymentForm.reference),
    statut: emptyToNull(paymentForm.statut),
    date_paiement: emptyToNull(paymentForm.date_paiement),
    notes: emptyToNull(paymentForm.notes),
  };

  try {
    if (editingPayment.value) {
      await updatePayment(editingPayment.value.id, payload);
      toast.success("Paiement mis à jour avec succès.");
    } else {
      await createPayment(payload);
      toast.success("Paiement créé avec succès.");
    }
    hidePaymentModal();
    await loadPayments();
  } catch (error) {
    handleError(error, "Une erreur est survenue lors de l'enregistrement du paiement.");
  } finally {
    paymentSubmitting.value = false;
  }
};

const confirmDeletePayment = async (payment) => {
  if (!window.confirm(`Supprimer le paiement "${payment.reference || payment.id}" ?`)) return;
  try {
    await deletePayment(payment.id);
    toast.success("Paiement supprimé avec succès.");
    await loadPayments();
  } catch (error) {
    handleError(error, "Impossible de supprimer le paiement.");
  }
};

const openReminderModal = (reminder = null) => {
  if (reminder) {
    editingReminder.value = reminder;
    reminderForm.abonnement_id = reminder.abonnement_id ?? reminder.abonnement?.id ?? "";
    reminderForm.societe_id = reminder.societe_id ?? reminder.societe?.id ?? "";
    reminderForm.type = reminder.type ?? "";
    reminderForm.canal = reminder.canal ?? "";
    reminderForm.date_envoi = toDateTimeInput(reminder.date_envoi);
    reminderForm.statut = reminder.statut ?? "";
  } else {
    editingReminder.value = null;
    resetReminderForm();
  }

  reminderModalInstance?.show();
};

const hideReminderModal = () => {
  reminderModalInstance?.hide();
};

const submitReminderForm = async () => {
  reminderSubmitting.value = true;
  const payload = {
    abonnement_id: Number(reminderForm.abonnement_id),
    societe_id: Number(reminderForm.societe_id),
    type: reminderForm.type,
    canal: reminderForm.canal,
    date_envoi: emptyToNull(reminderForm.date_envoi),
    statut: emptyToNull(reminderForm.statut),
  };

  try {
    if (editingReminder.value) {
      await updateReminder(editingReminder.value.id, payload);
      toast.success("Rappel mis à jour avec succès.");
    } else {
      await createReminder(payload);
      toast.success("Rappel créé avec succès.");
    }
    hideReminderModal();
    await loadReminders();
  } catch (error) {
    handleError(error, "Une erreur est survenue lors de l'enregistrement du rappel.");
  } finally {
    reminderSubmitting.value = false;
  }
};

const confirmDeleteReminder = async (reminder) => {
  if (!window.confirm(`Supprimer le rappel "${reminder.type}" ?`)) return;
  try {
    await deleteReminder(reminder.id);
    toast.success("Rappel supprimé avec succès.");
    await loadReminders();
  } catch (error) {
    handleError(error, "Impossible de supprimer le rappel.");
  }
};

const openUsageModal = (log = null) => {
  if (log) {
    editingUsage.value = log;
    usageForm.abonnement_id = log.abonnement_id ?? log.abonnement?.id ?? "";
    usageForm.societe_id = log.societe_id ?? log.societe?.id ?? "";
    usageForm.metric = log.metric ?? "";
    usageForm.valeur = log.valeur;
    usageForm.date_snapshot = toDateInput(log.date_snapshot);
  } else {
    editingUsage.value = null;
    resetUsageForm();
  }

  usageModalInstance?.show();
};

const hideUsageModal = () => {
  usageModalInstance?.hide();
};

const submitUsageForm = async () => {
  usageSubmitting.value = true;
  const payload = {
    abonnement_id: Number(usageForm.abonnement_id),
    societe_id: Number(usageForm.societe_id),
    metric: usageForm.metric,
    valeur: toNumberOrNull(usageForm.valeur),
    date_snapshot: emptyToNull(usageForm.date_snapshot),
  };

  try {
    if (editingUsage.value) {
      await updateUsageLog(editingUsage.value.id, payload);
      toast.success("Entrée d'utilisation mise à jour avec succès.");
    } else {
      await createUsageLog(payload);
      toast.success("Entrée d'utilisation créée avec succès.");
    }
    hideUsageModal();
    await loadUsageLogs();
  } catch (error) {
    handleError(error, "Une erreur est survenue lors de l'enregistrement de l'entrée d'utilisation.");
  } finally {
    usageSubmitting.value = false;
  }
};

const confirmDeleteUsage = async (log) => {
  if (!window.confirm(`Supprimer l'entrée "${log.metric}" ?`)) return;
  try {
    await deleteUsageLog(log.id);
    toast.success("Entrée d'utilisation supprimée avec succès.");
    await loadUsageLogs();
  } catch (error) {
    handleError(error, "Impossible de supprimer l'entrée d'utilisation.");
  }
};

const setActiveTab = (tab) => {
  activeTab.value = tab;
};

const getCollection = (tab) => {
  switch (tab) {
    case "plans":
      return plans.value;
    case "options":
      return options.value;
    case "invoices":
      return invoices.value;
    case "payments":
      return payments.value;
    case "reminders":
      return reminders.value;
    case "usage":
      return usageLogs.value;
    default:
      return [];
  }
};

const emptyToNull = (value) => {
  if (value === "" || value === undefined) {
    return null;
  }
  return value;
};

const toDateInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const toDateTimeInput = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 16);
};

const toNumberOrZero = (value) => {
  const number = Number(value);
  return Number.isNaN(number) ? 0 : number;
};

const toNumberOrNull = (value) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  const number = Number(value);
  return Number.isNaN(number) ? null : number;
};

const formatSocieteLabel = (societe) => {
  if (!societe) {
    return "Société inconnue";
  }

  const name = societe.societe_nom || `Société #${societe.id ?? "?"}`;
  const rc = societe.rc;
  const identifier = rc ? `RC: ${rc}` : `ID: ${societe.id ?? "?"}`;

  return `${name} (${identifier})`;
};

const formatCurrency = (value) => {
  if (value === null || value === undefined) return "—";
  const number = Number(value);
  if (Number.isNaN(number)) return value;
  return `${number.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })} MAD`;
};

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString("fr-FR");
};

const handleError = (error, fallbackMessage) => {
  const message = error?.response?.data?.message || error?.message || fallbackMessage;
  toast.error(message);
};

// add pagination reactive state
const page = ref(1);
const pageSize = ref(10);

const activeCollection = computed(() => {
  return getCollection(activeTab.value);
});

const totalItems = computed(() => (activeCollection.value ? activeCollection.value.length : 0));
const totalPages = computed(() => Math.max(1, Math.ceil(totalItems.value / pageSize.value)));

const pagedCollection = computed(() => {
  const list = activeCollection.value || [];
  const start = (page.value - 1) * pageSize.value;
  return list.slice(start, start + pageSize.value);
});

// visible pages window
const visiblePages = computed(() => {
  const total = totalPages.value;
  const current = Number(page.value || 1);
  const maxVisible = 7;
  if (total <= maxVisible) return Array.from({ length: total }, (_, i) => i + 1);
  const half = Math.floor(maxVisible / 2);
  let start = Math.max(1, current - half);
  let end = start + maxVisible - 1;
  if (end > total) {
    end = total;
    start = end - maxVisible + 1;
  }
  const pages = [];
  for (let i = start; i <= end; i++) pages.push(i);
  return pages;
});

const goToPage = (p) => {
  const target = Number(p);
  if (!Number.isFinite(target)) return;
  if (target < 1 || target > totalPages.value) return;
  page.value = target;
};

const changePageSize = () => {
  page.value = 1;
};

// reset page when collection changes
watch([activeCollection, pageSize], () => {
  if (page.value > totalPages.value) page.value = 1;
});

onMounted(async () => {
  try {
    await refreshAll();
  } catch (error) {
    handleError(error, "Impossible de charger les données de support.");
  }

  await nextTick();

  if (planModalRef.value) {
    planModalInstance = new Modal(planModalRef.value, modalOptions);
  }
  if (optionModalRef.value) {
    optionModalInstance = new Modal(optionModalRef.value, modalOptions);
  }
  if (invoiceModalRef.value) {
    invoiceModalInstance = new Modal(invoiceModalRef.value, modalOptions);
  }
  if (paymentModalRef.value) {
    paymentModalInstance = new Modal(paymentModalRef.value, modalOptions);
  }
  if (reminderModalRef.value) {
    reminderModalInstance = new Modal(reminderModalRef.value, modalOptions);
  }
  if (usageModalRef.value) {
    usageModalInstance = new Modal(usageModalRef.value, modalOptions);
  }
});

onBeforeUnmount(() => {
  planModalInstance?.dispose();
  planModalInstance = null;

  optionModalInstance?.dispose();
  optionModalInstance = null;

  invoiceModalInstance?.dispose();
  invoiceModalInstance = null;

  paymentModalInstance?.dispose();
  paymentModalInstance = null;

  reminderModalInstance?.dispose();
  reminderModalInstance = null;

  usageModalInstance?.dispose();
  usageModalInstance = null;
});
</script>
